import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { formValueSelector, getFormSyncErrors } from 'redux-form'
// constants
import { Forms, Modals } from 'constants/index'
// components
import Label from '../Label'
import Section from '../Section'
import FieldContent from '../FieldContent'
import HistoryButton from './components/HistoryButton'
import ControlButtons from '../ControlButtons'
import AiControlButton from '../AiControlButton'
import InjuryMasterLinks from '../InjuryMasterLinks'
// containers
import RiskTableContainer from 'containers/Risks/RiskTable'
// types
import { IRiskFieldValue } from 'types/risk'
// hooks
import { useFieldType, useSaveFieldValue, useShowModal } from 'hooks'
// types
import { IField } from 'types/graphql'
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { FieldWrapper } from './views'
// utils
import { get, orderBy } from 'lodash'
import { getIconForProperty } from 'utils/fields'

interface Props {
  field: IField
  onFieldUpdate?: (field: IField, value: IRiskFieldValue) => void
  dragHandleProps?: DraggableProvidedDragHandleProps
}

const selector = formValueSelector(Forms.RISK_TEMPLATE_FORM)

const RiskTemplateField: React.FC<Props> = ({ field, onFieldUpdate, dragHandleProps }) => {
  const { fieldValue, error } = useSelector((state) => ({
    fieldValue: selector(state, field.id),
    error: getFormSyncErrors(Forms.RISK_TEMPLATE_FORM)(state)[field.id]
  }))

  const { riskTemplate, isRiskTemplate, deleteField } = React.useContext(RiskTemplateAPIContext)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const { isTable, isLabel, isMatrix, isSection, isSwitch, isRecordLinks, isParentFields, isInjuryMasterLinks } =
    useFieldType(field)

  useSaveFieldValue({ name: field.id, value: fieldValue, field, onFieldUpdate })

  const onDeleteClick = () => {
    showModal({
      width: 'sm',
      entity: 'field',
      handleConfirm: () => deleteField(field)
    })
  }

  if (isLabel) {
    return <Label field={field} dragHandleProps={dragHandleProps} />
  }

  if (isTable) {
    const fields = orderBy(
      riskTemplate.fields.filter(({ parentFieldId }) => parentFieldId === field.id),
      'position'
    )

    return <RiskTableContainer field={field} fields={fields} dragHandleProps={dragHandleProps} />
  }

  if (isSection) {
    const fields = orderBy(
      riskTemplate.fields.filter(({ parentFieldId }) => parentFieldId === field.id),
      'position'
    )

    return <Section field={field} fields={fields} dragHandleProps={dragHandleProps} />
  }

  if (isInjuryMasterLinks) {
    return <InjuryMasterLinks field={field} dragHandleProps={dragHandleProps} />
  }

  return (
    <FieldWrapper
      $isError={!!error}
      $isSwitch={isSwitch}
      $isMatrix={isMatrix}
      $isRecordLinks={isRecordLinks || isParentFields}
      $isRiskTemplate={isRiskTemplate}
      $historyEnabled={field.historyEnabled}
      $color={get(fieldValue, 'selectedOption.color')}
      data-testid='risk-template-field-row'
      data-entity-id={field.id}>
      {isRiskTemplate && <span {...dragHandleProps}>{getIconForProperty(field.fieldType)}</span>}
      <FieldContent name={`${field.name}.value`} field={field} />
      <HistoryButton field={field} fieldValueId={fieldValue?.fieldValueId} />
      {isRiskTemplate && <ControlButtons field={field} onDeleteClick={onDeleteClick} />}
      {riskTemplate.aiEnabled && !isRiskTemplate && <AiControlButton field={field} />}
    </FieldWrapper>
  )
}

export default RiskTemplateField
